// IndexPage — the home page of the standalone help portal at "/".
//
// Structure (top → bottom):
//   1. Search-first hero with prominent input + popular-search chips
//   2. Browse-by-role card grid grouped into three audience sections
//   3. "Still need help?" support callout with email / phone CTAs
//
// The hero search input opens the existing search modal — we don't
// duplicate the search logic here, we just trigger it.

import { Link } from 'react-router-dom';
import { openSearch } from '../components/SearchModal';

// ----- Card data model -----
interface RoleCard {
  to: string;
  icon: string;
  title: string;
  desc: string;
}

interface CardSection {
  title: string;
  intro: string;
  cards: RoleCard[];
}

// ----- Card data, grouped by audience -----
// If you add a new role guide, add an entry to the appropriate section AND
// to Footer's KB_LINKS AND to App.tsx's ROUTES.
const SECTIONS: CardSection[] = [
  {
    title: 'For Daily Users',
    intro: 'Guides for the people who use the system every day to teach, learn, and stay informed.',
    cards: [
      {
        to: '/teacher',
        icon: '👩‍🏫',
        title: "Teachers' Guide",
        desc: 'Mark attendance, manage lesson plans, enter marks, schedule tests, write daily diary entries, and communicate with parents.',
      },
      {
        to: '/parent',
        icon: '👨‍👩‍👧',
        title: "Parents' Guide",
        desc: "Track your child's attendance and progress, view fee details, pay online, read daily diary and announcements.",
      },
    ],
  },
  {
    title: 'For Leadership',
    intro: 'Management and oversight roles that monitor school-level operations.',
    cards: [
      {
        to: '/principal',
        icon: '🎓',
        title: 'Principal & Coordinator Guide',
        desc: 'Monitor attendance and academic progress, review approvals, manage substitutions, and access management-level reports.',
      },
      {
        to: '/counsellor',
        icon: '🧠',
        title: 'Counsellor Guide',
        desc: 'Manage appointments, session notes, teacher concerns, and referrals — with confidentiality safeguards.',
      },
      {
        to: '/admin',
        icon: '⚙️',
        title: 'System Administrator Guide',
        desc: 'Configure modules, manage user rights, set up academic and financial years, import master data, and administer settings.',
      },
    ],
  },
  {
    title: 'For Operations',
    intro: 'Specialist office and administrative roles.',
    cards: [
      {
        to: '/accountant',
        icon: '💼',
        title: 'Accountant & Cashier Guide',
        desc: 'Collect fees, issue receipts, post vouchers, manage deposits and refunds, reconcile online payments.',
      },
      {
        to: '/hr',
        icon: '🧑‍💼',
        title: 'HR & Payroll Guide',
        desc: 'Manage employee records, attendance, leave, organisational structure, and monthly salary processing.',
      },
      {
        to: '/librarian',
        icon: '📚',
        title: 'Librarian Guide',
        desc: 'Catalogue books, manage issues and returns, charge fines, and produce circulation reports.',
      },
      {
        to: '/transport',
        icon: '🚌',
        title: 'Transport Guide',
        desc: 'Set up routes and stages, allocate students, raise transport demand, and run transport reports.',
      },
      {
        to: '/admissions',
        icon: '📥',
        title: 'Admissions & Enquiry Guide',
        desc: 'Capture enquiries, allocate to staff, run follow-ups, and convert leads to admissions.',
      },
      {
        to: '/reception',
        icon: '🛎️',
        title: 'Visitor Desk Guide',
        desc: 'Log visitors, generate ID cards, manage check-ins and check-outs, and produce daily visitor reports.',
      },
    ],
  },
];

// ----- Popular search chips -----
// These are the queries we expect users to land on the help portal looking
// for. Clicking a chip opens the modal pre-filled with that query so the
// user sees relevant results immediately. Tune the list based on real
// search analytics once available.
const POPULAR_SEARCHES: string[] = [
  'Mark attendance',
  'Pay fees online',
  'Reset password',
  'Generate report card',
  'Apply for leave',
];

// Cmd/Ctrl shortcut hint. We can't detect the user's OS reliably from
// userAgent in a clean way without false positives, so we just always
// show ⌘K — Windows users still know Ctrl+K from the navbar's tooltip.
const SHORTCUT_HINT = '⌘K';

export default function IndexPage() {
  return (
    <>
      {/* ===== Search-first hero ===== */}
      <section className="home-hero">
        <div className="home-hero__content">
          <h1 className="home-hero__title">
            How can we <span className="accent">help?</span>
          </h1>
          <p className="home-hero__subtitle">
            Step-by-step guides, tutorials, and answers tailored to your role.
            Type below to search, or browse by role.
          </p>

          {/* The "input" is a div containing a real <input>. Clicking the
              whole capsule focuses the inner field and opens the search
              modal. The actual querying still happens in <SearchModal>. */}
          <div
            className="hero-search"
            onClick={() => openSearch()}
            role="search"
          >
            <span className="hero-search__icon" aria-hidden="true">🔍</span>
            <input
              type="text"
              className="hero-search__input"
              placeholder="Search guides, features, FAQs…"
              // We don't actually capture input here — focusing this input
              // opens the modal, which has its own input. We use readOnly
              // to prevent React from controlling it and to make the
              // intent (this is a search trigger) unambiguous.
              readOnly
              onFocus={() => openSearch()}
              aria-label="Search the knowledge base"
            />
            <span className="hero-search__shortcut" aria-hidden="true">
              {SHORTCUT_HINT}
            </span>
          </div>

          {/* Popular search chips. Each chip opens the modal pre-filled
              with that query, showing results immediately. */}
          <div className="hero-quick-links">
            <span className="hero-quick-links__label">Popular:</span>
            {POPULAR_SEARCHES.map((q) => (
              <button
                key={q}
                type="button"
                className="hero-quick-links__chip"
                onClick={() => openSearch(q)}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Browse-by-role card grid ===== */}
      <main>
        {SECTIONS.map((section) => (
          <div key={section.title} className="kb-section">
            <div className="kb-section__header">
              <h2>{section.title}</h2>
              <p>{section.intro}</p>
            </div>
            <div className="kb-cards-grid">
              {section.cards.map((card) => (
                <Link key={card.to} to={card.to} className="kb-card">
                  <div className="kb-card__icon">{card.icon}</div>
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                  <span className="kb-card__link">Open Guide →</span>
                </Link>
              ))}
            </div>
          </div>
        ))}

        {/* ===== "Still need help?" support callout ===== */}
        <section className="home-support">
          <h2>Can't find what you're looking for?</h2>
          <p>Our support team is here to help with anything the docs don't cover.</p>
          <div className="home-support__actions">
            <a className="home-support__cta" href="mailto:info@myeduate.com">
              📧 Email support
            </a>
            <a className="home-support__cta-secondary" href="tel:9844774400">
              📞 (+91) 9844774400
            </a>
          </div>
        </section>
      </main>
    </>
  );
}
